import { FC, useState } from "react";
import { BellIcon, BellOffIcon } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { SubscriptionCategory } from "@/models/subscriptions/Subscription";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { useSubscribeAction } from "@/hooks/useSubscribeAction";
import LegislationSubscribeModal, {
  type LegislationMetadataPreview,
} from "@/components/modals/LegislationSubscribeModal";

export type { LegislationMetadataPreview };

type SubscribeButtonProps = {
  subscribeCategory: SubscriptionCategory;
  entityPath: string;
  entityName: string;
  source: string;
  /** Legislation variant: externalId for create. */
  legislationExternalId?: string;
  /** Legislation variant: datasource for create. */
  legislationDatasource?: string;
  /** Legislation variant: when subscribed, id for remove. */
  legislationSubscriptionId?: string;
  /** Legislation variant: shown in subscribe modal. */
  legislationMetadata?: LegislationMetadataPreview;
  className?: string;
  strokeWidth?: number;
  container?: HTMLElement;
  onOperationStart?: () => void;
  onOperationEnd?: () => void;
  isSubscribed: boolean;
};

const SubscribeButton: FC<SubscribeButtonProps> = ({
  subscribeCategory,
  entityPath,
  entityName,
  source,
  legislationExternalId,
  legislationDatasource,
  legislationSubscriptionId,
  legislationMetadata,
  className,
  strokeWidth = 2,
  container,
  onOperationStart,
  onOperationEnd,
  isSubscribed,
}) => {
  const {
    activate,
    isLoading,
    label,
    isLegislationModalOpen,
    setIsLegislationModalOpen,
    confirmLegislationSubscribe,
  } = useSubscribeAction({
    subscribeCategory,
    entityPath,
    entityName,
    source,
    isSubscribed,
    legislationExternalId,
    legislationDatasource,
    legislationSubscriptionId,
    legislationMetadata,
    onOperationStart,
    onOperationEnd,
  });

  const [isHoveringButton, setIsHoveringButton] = useState(false);

  const buttonContent = (
    <>
      {isHoveringButton && isSubscribed ? (
        <BellOffIcon
          className="size-4 fill-warning"
          strokeWidth={strokeWidth}
        />
      ) : (
        <BellIcon
          className={`size-4 ${
            isSubscribed
              ? "fill-warning stroke-warning"
              : isHoveringButton
                ? "fill-warning"
                : ""
          }`}
          strokeWidth={strokeWidth}
        />
      )}
    </>
  );

  const buttonClassName = className + " p-2";
  const hoverProps = {
    onMouseEnter: () => setIsHoveringButton(true),
    onMouseLeave: () => setIsHoveringButton(false),
  };

  if (isLoading) {
    return (
      <Button
        variant="ghost"
        size="icon"
        className={buttonClassName}
        onClick={(e) => e.stopPropagation()}
      >
        <Spinner className="size-4" />
      </Button>
    );
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={buttonClassName}
            disabled={isLoading}
            {...hoverProps}
            onClick={async (e) => {
              e.stopPropagation();
              await activate();
              setIsHoveringButton(false);
            }}
          >
            {buttonContent}
          </Button>
        </TooltipTrigger>
        <TooltipContent container={container}>{label}</TooltipContent>
      </Tooltip>
      {subscribeCategory === SubscriptionCategory.Legislation &&
        !isSubscribed && (
          <LegislationSubscribeModal
            open={isLegislationModalOpen}
            onOpenChange={setIsLegislationModalOpen}
            entityName={entityName}
            legislationExternalId={legislationExternalId}
            legislationDatasource={legislationDatasource}
            legislationMetadata={legislationMetadata}
            isConfirmLoading={isLoading}
            container={container}
            onConfirm={confirmLegislationSubscribe}
          />
        )}
    </>
  );
};

export default SubscribeButton;
